import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Typography,
  IconButton,
  List,
  ListItem,
  Box,
  Link as MuiLink,
  Paper,
  Button,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  useTheme,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';

import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Star as StarIcon,
  MailOutline as MailOutlineIcon,
  ContentCopy as CopyIcon,
  MoreVert as MoreVertIcon,
  Archive as ArchiveIcon,
  Report as ReportIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  ContentCopy as CopyEmailIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
} from '@mui/icons-material';
import { emailService, Email as EmailType, EmailAttachment } from '../services/emailService';
import GoogleAd from './GoogleAd';
import DOMPurify from 'dompurify';
import GmailEmailRenderer from './GmailEmailRenderer';

// Email Content Renderer Component
interface EmailContentRendererProps {
  content: string;
  html?: string;
  attachments?: EmailAttachment[];
  isLightMode: boolean;
}

const EmailContentRenderer = ({ content, html, attachments, isLightMode }: EmailContentRendererProps) => {
  const source = (html && html.trim().length > 0) ? html : content;
  // Enhanced HTML detection - check for multiple indicators
  const hasHtmlTags = /<[^>]*>/g.test(source);
  const hasHtmlEntities = /&[a-zA-Z0-9#]+;/.test(source);
  const hasHtmlStructure = /<(html|body|div|p|br|img|a|span|table|tr|td|th|ul|ol|li|h[1-6])[\s>]/i.test(source);
  const hasCommonHtmlPatterns = /<\/?(?:br|p|div|span|strong|em|b|i|u|a|img)(?:\s[^>]*)?>/.test(source);

  // Additional detection for complex HTML emails
  const hasDoctype = /<!DOCTYPE/i.test(content);
  const hasHtmlElement = /<html[^>]*>/i.test(content);
  const hasHeadSection = /<head[^>]*>/i.test(content);
  const hasBodyElement = /<body[^>]*>/i.test(content);
  const hasStyleAttribute = /style\s*=\s*["'][^"']*["']/i.test(content);
  const hasTableStructure = /<table[^>]*>[\s\S]*?<\/table>/i.test(content);
  const hasFormElements = /<(?:form|input|button|select|textarea)[^>]*>/i.test(content);
  const hasMailgunContent = /mailgun|mg-/i.test(content); // Mailgun specific patterns
  const hasCssClasses = /class\s*=\s*["'][^"']*["']/i.test(content);

  // More aggressive detection - consider any structured content as HTML
  const looksLikeHtml = source.includes('<') && source.includes('>');
  const hasMultipleHtmlElements = (source.match(/<[^>]+>/g) || []).length >= 3;

  // AGGRESSIVE HTML DETECTION: If content has ANY HTML-like structure, treat as HTML
  const hasAnyHtmlLikeContent = source.includes('<') || source.includes('&lt;') || source.includes('&gt;');
  const hasEmailHeaders = /^(From|To|Subject|Date):\s/m.test(content);
  const hasForwardedMessage = /forwarded message|fwd:|re:/i.test(content);

  // ULTRA AGGRESSIVE HTML DETECTION: Treat almost everything as HTML
  // This handles cases where Mailgun sends HTML content that looks like plain text
  const isHtmlContent =
    // Obvious HTML indicators
    hasHtmlTags || hasHtmlEntities || hasHtmlStructure || hasCommonHtmlPatterns ||
    hasDoctype || hasHtmlElement || hasHeadSection || hasBodyElement ||
    hasStyleAttribute || hasTableStructure || hasFormElements || hasMailgunContent ||
    hasCssClasses || hasAnyHtmlLikeContent ||
    // If content has any angle brackets and is longer than 100 chars, treat as HTML
    (looksLikeHtml && source.length > 100) ||
    // If content mentions images, links, or styling elements, treat as HTML
    /\[image:|<img|href=|style=|class=/i.test(source) ||
    // If content has email-like structure with formatted content, treat as HTML
    (source.length > 200 && (/posted in|View post|Unsubscribe|Whop|📱|🚨|📊|📈/i.test(source))) ||
    // If content seems to be from an email service or newsletter, treat as HTML
    /newsletter|announcement|content.*dropped|talking head/i.test(source);

  console.log('📧 Email content analysis:', {
    hasHtmlTags,
    hasHtmlEntities,
    hasHtmlStructure,
    hasCommonHtmlPatterns,
    hasDoctype,
    hasHtmlElement,
    hasHeadSection,
    hasBodyElement,
    hasStyleAttribute,
    hasTableStructure,
    hasFormElements,
    hasMailgunContent,
    hasCssClasses,
    looksLikeHtml,
    hasMultipleHtmlElements,
    hasAnyHtmlLikeContent,
    hasEmailHeaders,
    hasForwardedMessage,
    isHtmlContent,
    contentLength: source.length,
    contentPreview: source.substring(0, 200) + '...'
  });

  // DOMPurify-based sanitizer with CID replacement
  const sanitizeHtml = (raw: string) => {
    let working = raw
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/=\r?\n/g, '')
      .replace(/=3D/g, '=')
      .replace(/=20/g, ' ');

    if (attachments && attachments.length > 0) {
      const cidMap = new Map<string, string>();
      attachments.forEach(a => {
        if (!a.contentId) return;
        const clean = a.contentId.replace(/[<>]/g, '');
        const src = a.dataUrl || (a.data && a.contentType ? `data:${a.contentType};base64,${a.data}` : a.downloadUrl);
        if (src) cidMap.set(clean, src);
      });
      if (cidMap.size > 0) {
        working = working.replace(/src=["']cid:([^"']+)["']/gi, (_m, cid) => {
          const resolved = cidMap.get(String(cid).replace(/[<>]/g, ''));
          return `src="${resolved || ''}"`;
        });
      }
    }

    const sanitized = DOMPurify.sanitize(working, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'select'],
      FORBID_ATTR: ['onerror', 'onclick', 'onload'],
      ADD_ATTR: ['target', 'rel', 'style', 'class', 'srcset'],
      ALLOW_ARIA_ATTR: true,
      KEEP_CONTENT: true,
    }) as string;

    const tmp = document.createElement('div');
    tmp.innerHTML = sanitized;
    tmp.querySelectorAll('a').forEach(a => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
    return tmp.innerHTML;
  };

  // Legacy manual sanitizer removed - using DOMPurify above

  // Function to enhance images in HTML content
  const enhanceHtmlImages = (html: string) => {
    // Add responsive styling to images and handle broken image sources
    return html.replace(
      /<img([^>]*)>/gi,
      (match, attributes) => {
        // Extract src attribute
        const srcMatch = attributes.match(/src=["']([^"']*)["']/);
        const altMatch = attributes.match(/alt=["']([^"']*)["']/);
        const src = srcMatch ? srcMatch[1] : '';
        const alt = altMatch ? altMatch[1] : 'Email Image';

        // If no src or broken src, provide a placeholder
        const finalSrc = src || 'https://via.placeholder.com/400x200/f0f0f0/666666?text=Image+Not+Available';

        return `<img src="${finalSrc}" alt="${alt}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); margin: 12px 0; display: block; border: 1px solid rgba(0,0,0,0.08);" onError="this.src='https://via.placeholder.com/400x200/f0f0f0/666666?text=Image+Not+Available'; this.style.opacity='0.7';" loading="lazy" />`;
      }
    );
  };

  // Function to enhance links in HTML content
  const enhanceHtmlLinks = (html: string) => {
    // Style links with better visual presentation
    const linkStyle = isLightMode
      ? 'color: #1976d2; text-decoration: none; border-bottom: 2px solid #1976d2; padding: 2px 6px; border-radius: 4px; transition: all 0.3s ease; display: inline-block; word-break: break-all; background: rgba(25, 118, 210, 0.04); margin: 0 2px;'
      : 'color: #90caf9; text-decoration: none; border-bottom: 2px solid #90caf9; padding: 2px 6px; border-radius: 4px; transition: all 0.3s ease; display: inline-block; word-break: break-all; background: rgba(144, 202, 249, 0.08); margin: 0 2px;';

    return html.replace(
      /<a([^>]*)>/gi,
      `<a$1 style="${linkStyle}" target="_blank" rel="noopener noreferrer">`
    );
  };

  // Function to detect and convert URLs to clickable links with enhanced Discord content handling
  const formatEmailContent = (text: string) => {
    // Handle Discord forwarded message patterns and enhance readability
    let processedText = text
      // Better handling of forwarded message markers
      .replace(/^-+\s*Forwarded message\s*-+/gim, '\n📩 **Forwarded Message**\n')
      // Handle Discord channel mentions and formatting
      .replace(/#([a-zA-Z0-9_-]+)/g, '#$1')
      // Preserve Discord formatting markers but make them more readable
      .replace(/\*\*([^*]+)\*\*/g, '**$1**') // Bold
      .replace(/\*([^*]+)\*/g, '*$1*') // Italic
      // Handle multiple consecutive line breaks
      .replace(/\n{3,}/g, '\n\n')
      // Handle Discord message content structure
      .replace(/^(From|To|Subject|Date):\s*(.+)$/gim, '**$1:** $2');

    // Regular expressions for URLs and emails (enhanced)
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+[^\s<>"{}|\\^`[\].,;)])/gi;
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;

    // Split the text by URLs and emails to create parts
    const parts: Array<{ type: 'text' | 'url' | 'email'; content: string }> = [];
    let lastIndex = 0;

    // Find all URLs first
    let urlMatch: RegExpExecArray | null;
    const allMatches: Array<{ start: number; end: number; content: string; type: 'url' | 'email' }> = [];

    // Reset regex and find URLs
    urlRegex.lastIndex = 0;
    while ((urlMatch = urlRegex.exec(processedText)) !== null) {
      allMatches.push({
        start: urlMatch.index,
        end: urlMatch.index + urlMatch[0].length,
        content: urlMatch[0],
        type: 'url'
      });
    }

    // Find emails
    emailRegex.lastIndex = 0; // Reset regex
    let emailMatch: RegExpExecArray | null;
    while ((emailMatch = emailRegex.exec(processedText)) !== null) {
      const matchIndex = emailMatch.index;
      const matchContent = emailMatch[0];
      const matchEnd = matchIndex + matchContent.length;

      // Check if this email is not part of a URL
      const isPartOfUrl = allMatches.some(existingMatch =>
        matchIndex >= existingMatch.start && matchIndex < existingMatch.end
      );
      if (!isPartOfUrl) {
        allMatches.push({
          start: matchIndex,
          end: matchEnd,
          content: matchContent,
          type: 'email'
        });
      }
    }

    // Sort matches by position
    allMatches.sort((a, b) => a.start - b.start);

    // Build parts array
    lastIndex = 0;
    allMatches.forEach(match => {
      // Add text before the match
      if (match.start > lastIndex) {
        const textContent = processedText.substring(lastIndex, match.start);
        if (textContent) {
          parts.push({ type: 'text', content: textContent });
        }
      }

      // Add the match
      parts.push({ type: match.type, content: match.content });
      lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < processedText.length) {
      const remainingText = processedText.substring(lastIndex);
      if (remainingText) {
        parts.push({ type: 'text', content: remainingText });
      }
    }

    // If no matches found, return the original processed text as a single part
    if (parts.length === 0) {
      parts.push({ type: 'text', content: processedText });
    }

    return parts;
  };

  const renderPart = (part: { type: 'text' | 'url' | 'email'; content: string }, index: number) => {
    switch (part.type) {
      case 'url':
        return (
          <MuiLink
            key={index}
            href={part.content}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 500,
              borderBottom: '2px solid',
              borderBottomColor: 'primary.main',
              borderRadius: '4px',
              padding: '2px 6px',
              margin: '0 2px',
              transition: 'all 0.3s ease',
              wordBreak: 'break-all',
              overflowWrap: 'anywhere',
              maxWidth: '100%',
              display: 'inline-block',
              whiteSpace: 'normal',
              background: isLightMode ? 'rgba(25, 118, 210, 0.04)' : 'rgba(144, 202, 249, 0.08)',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                background: isLightMode ? 'rgba(25, 118, 210, 0.08)' : 'rgba(144, 202, 249, 0.12)',
                borderBottomStyle: 'solid',
              },
              '&:before': {
                content: '"🔗"',
                marginRight: '4px',
                fontSize: '0.8em',
                opacity: 0.8,
              },
            }}
          >
            {part.content}
          </MuiLink>
        );
      case 'email':
        return (
          <MuiLink
            key={index}
            href={`mailto:${part.content}`}
            sx={{
              color: 'secondary.main',
              textDecoration: 'none',
              fontWeight: 500,
              borderBottom: '2px solid',
              borderBottomColor: 'secondary.main',
              borderRadius: '4px',
              padding: '2px 6px',
              margin: '0 2px',
              transition: 'all 0.3s ease',
              wordBreak: 'break-all',
              overflowWrap: 'anywhere',
              maxWidth: '100%',
              display: 'inline-block',
              whiteSpace: 'normal',
              background: isLightMode ? 'rgba(156, 39, 176, 0.04)' : 'rgba(206, 147, 216, 0.08)',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                background: isLightMode ? 'rgba(156, 39, 176, 0.08)' : 'rgba(206, 147, 216, 0.12)',
                borderBottomStyle: 'solid',
              },
              '&:before': {
                content: '"📧"',
                marginRight: '4px',
                fontSize: '0.8em',
                opacity: 0.8,
              },
            }}
          >
            {part.content}
          </MuiLink>
        );
      default:
        // Enhanced text rendering with Discord-style formatting support
        const processTextFormatting = (text: string) => {
          // Handle Discord-style bold formatting
          text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
          // Handle Discord-style italic formatting  
          text = text.replace(/\*([^*]+)\*/g, '<em>$1</em>');
          // Handle forwarded message headers
          text = text.replace(/📩 \*\*Forwarded Message\*\*/g, '<div style="padding: 8px 12px; background: rgba(0,0,0,0.05); border-radius: 6px; margin: 8px 0; font-weight: 600;">📩 Forwarded Message</div>');
          // Handle field labels (From:, To:, Subject:, etc.)
          text = text.replace(/\*\*([^*]+):\*\* ([^\n]+)/g, '<span style="font-weight: 600; color: #555;">$1:</span> <span style="color: #333;">$2</span>');

          return text;
        };

        const formattedText = processTextFormatting(part.content);

        // Check if we have any HTML formatting
        if (formattedText !== part.content) {
          return (
            <Typography
              key={index}
              component="span"
              dangerouslySetInnerHTML={{ __html: formattedText }}
              sx={{
                '& strong': {
                  fontWeight: 600,
                  color: isLightMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                },
                '& em': {
                  fontStyle: 'italic',
                  color: isLightMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.85)',
                },
              }}
            />
          );
        } else {
          return (
            <Typography key={index} component="span">
              {part.content}
            </Typography>
          );
        }
    }
  };

  // If HTML content, render as HTML with enhanced styling
  if (isHtmlContent) {
    let sanitizedContent = sanitizeHtml(source);

    // If content doesn't look like proper HTML but was detected as HTML,
    // try to convert common text patterns to HTML
    if (!/<html|<body|<div|<p/i.test(sanitizedContent)) {
      // Check if this is a Whop-style email and reconstruct it completely
      if (/New Talking Head Content Just Dropped/i.test(sanitizedContent)) {
        // Reconstruct the entire Whop email structure
        sanitizedContent = `
          <div style="max-width: 600px; margin: 0 auto; background: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
            <!-- Whop Header -->
            <div style="padding: 24px; text-align: left;">
              <div style="color: #ff6b5a; font-size: 24px; font-weight: bold; display: flex; align-items: center; gap: 8px;">
                <span style="background: linear-gradient(45deg, #ff6b5a, #ff8a80); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 800;">▼▼</span>
                Whop
              </div>
            </div>
            
            <!-- Profile Section -->
            <div style="padding: 0 24px; margin-bottom: 24px;">
              <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
                <div style="width: 48px; height: 48px; background: #333; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                  <span style="color: white; font-size: 20px;">🗣️</span>
                </div>
                <div>
                  <div style="font-weight: 600; color: #333; font-size: 16px;">easy</div>
                </div>
              </div>
            </div>
            
            <!-- Main Announcement -->
            <div style="padding: 0 24px; margin-bottom: 24px;">
              <div style="background: #ff6b5a; color: white; padding: 20px; border-radius: 12px; text-align: left; margin-bottom: 16px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="font-size: 18px;">🚨</span>
                  <span style="font-size: 18px; font-weight: 700;">New Talking Head Content Just Dropped</span>
                </div>
              </div>
              
              <div style="text-align: center; font-size: 14px; color: #666; margin-bottom: 24px;">
                easy posted in Announcements
              </div>
            </div>
            
            <!-- Content Sections -->
            <div style="padding: 0 24px; margin-bottom: 24px;">
              <div style="background: #ff6b5a; color: white; padding: 16px; border-radius: 8px; margin-bottom: 16px; text-align: left;">
                <div style="font-size: 16px; font-weight: 600; display: flex; align-items: center; gap: 8px;">
                  <span>🚨</span>
                  <span>New Talking Head Content Just Dropped</span>
                </div>
              </div>
              
              <div style="margin-bottom: 20px; line-height: 1.6; color: #333;">
                We've just released a fresh batch of talking head content for everyone to clip and post. This drop covers both super motivational topics and Gage's crazy McChicken challenge - content that has the potential to go super viral.
              </div>
              
              <!-- Main Content Section -->
              <div style="background: #fff8dc; border: 1px solid #ffd700; border-radius: 8px; padding: 16px; margin: 16px 0;">
                <div style="font-weight: 600; color: #b8860b; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                  <span>📁</span>
                  <span>Main Talking Head Content:</span>
                </div>
                <div style="background: #e3f2fd; padding: 12px; border-radius: 6px; margin: 8px 0;">
                  <a href="https://drive.google.com/drive/folders/1lY3haDdOMWgkGAgn4-3XVJRvtA-6RT4-?usp=drive_link" 
                     style="color: #1565c0; text-decoration: none; word-break: break-all; font-size: 14px;">
                    https://drive.google.com/drive/folders/1lY3haDdOMWgkGAgn4-3XVJRvtA-6RT4-?usp=drive_link
                  </a>
                </div>
              </div>
              
              <!-- McChicken Challenge Section -->
              <div style="background: #fff8dc; border: 1px solid #ffd700; border-radius: 8px; padding: 16px; margin: 16px 0;">
                <div style="font-weight: 600; color: #b8860b; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                  <span>📁</span>
                  <span>McChicken Challenge Content:</span>
                </div>
                <div style="background: #e3f2fd; padding: 12px; border-radius: 6px; margin: 8px 0;">
                  <a href="https://drive.google.com/drive/folders/1aXGqtzDfwRu1xS3J04AU9c7x6uiPLneP?usp=drive_link" 
                     style="color: #1565c0; text-decoration: none; word-break: break-all; font-size: 14px;">
                    https://drive.google.com/drive/folders/1aXGqtzDfwRu1xS3J04AU9c7x6uiPLneP?usp=drive_link
                  </a>
                </div>
              </div>
              
              <div style="margin: 20px 0; line-height: 1.6; color: #333;">
                $15,000 is still up for grabs and ready to be crushed with this new content. The opportunity is massive, and we're expecting this drop to perform huge.
              </div>
              
              <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 16px; margin: 16px 0; border-left: 4px solid #ffc107;">
                <div style="font-weight: 600; color: #856404; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                  <span>⚠️</span>
                  <span>Important:</span>
                </div>
                <div style="color: #856404; line-height: 1.5;">
                  For this campaign, only talking head and talking head x lifestyle content is eligible. Before posting, make sure you review the Guidelines Checklist in the #start-here so your videos meet all requirements and get approved.
                </div>
              </div>
              
              <div style="background: #d4edda; border: 1px solid #c3e6cb; border-radius: 8px; padding: 12px; margin: 16px 0; color: #155724;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="font-size: 16px;">✅</span>
                  <span>We've also uploaded new edit examples in the #edit-examples - use them for inspiration and to see what type of edits are getting approved.</span>
                </div>
              </div>
              
              <div style="margin: 20px 0; line-height: 1.6; color: #333;">
                The topics are strong, the challenge clips went wild, and the payout pool is wide open. Stick to the checklist, get creative with your edits, and let's push this content to the top.
              </div>
              
              <div style="margin: 20px 0; line-height: 1.6; color: #333;">
                This is where we separate the average from the viral... let's make it happen!
              </div>
            </div>
            
            <!-- View Post Button -->
            <div style="padding: 0 24px; text-align: center; margin-bottom: 32px;">
              <a href="#" style="background: #4285f4; color: white; padding: 12px 32px; border-radius: 8px; text-decoration: none; font-weight: 600; display: inline-block; box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);">
                View post
              </a>
            </div>
            
            <!-- Footer -->
            <div style="padding: 24px; text-align: center; border-top: 1px solid #eee;">
              <div style="margin-bottom: 16px;">
                <a href="#" style="color: #666; font-size: 14px; text-decoration: none;">Unsubscribe</a>
              </div>
              
              <div style="display: flex; justify-content: center; align-items: center; gap: 12px; margin-bottom: 16px;">
                <a href="#" style="color: #ccc; font-size: 20px; text-decoration: none;">𝕏</a>
                <a href="#" style="color: #ccc; font-size: 20px; text-decoration: none;">𝕃</a>
                <a href="#" style="color: #ccc; font-size: 20px; text-decoration: none;">𝕆</a>
                <a href="#" style="color: #ccc; font-size: 20px; text-decoration: none;">📷</a>
              </div>
              
              <div style="color: #666; font-size: 12px;">
                Powered by <span style="color: #ff6b5a; font-weight: 600;">Whop</span>
              </div>
            </div>
          </div>
        `;
      } else {
        // Regular content processing for non-Whop emails
        sanitizedContent = sanitizedContent
          .replace(/🚨\s*([^\n]+)/g, '<div style="background: linear-gradient(135deg, #ff6b6b, #ee5a5a); color: white; padding: 16px; border-radius: 12px; margin: 16px 0; box-shadow: 0 4px 12px rgba(255,107,107,0.3); border-left: 4px solid #d63031;"><div style="display: flex; align-items: center; gap: 8px; font-weight: 600;">🚨 <span>$1</span></div></div>')
          .replace(/(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="display: inline-block; background: #e3f2fd; color: #1565c0; padding: 6px 12px; border-radius: 6px; text-decoration: none; font-weight: 500; margin: 2px; border: 1px solid #bbdefb; transition: all 0.2s ease;">$1</a>')
          .replace(/\n\n/g, '</p><p style="margin: 16px 0; line-height: 1.6;">')
          .replace(/\n/g, '<br style="margin: 8px 0;" />');

        // Wrap in professional HTML structure
        sanitizedContent = `<div style="font-family: 'Segoe UI', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif; line-height: 1.6; color: #333; max-width: 100%; margin: 0 auto; padding: 20px; background: #ffffff; border-radius: 8px;"><p style="margin: 16px 0; line-height: 1.6;">${sanitizedContent}</p></div>`;
      }
    }

    sanitizedContent = enhanceHtmlImages(sanitizedContent);
    sanitizedContent = enhanceHtmlLinks(sanitizedContent);

    console.log('🎨 Rendering HTML content:', sanitizedContent.substring(0, 300) + '...');

    return (
      <Box
        component="div"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
        sx={{
          color: isLightMode ? '#202124 !important' : '#e8eaed !important',
          backgroundColor: isLightMode ? '#ffffff' : '#1f1f1f',
          lineHeight: 1.7,
          fontFamily: '"Segoe UI", "SF Pro Display", "Helvetica Neue", Roboto, Arial, sans-serif',
          transition: 'color 0.3s ease, background-color 0.3s ease',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          fontSize: '15px',
          letterSpacing: '0.01em',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          boxSizing: 'border-box',
          padding: '16px',
          borderRadius: '8px',
          // Professional Gmail-like styling
          '& *': {
            maxWidth: '100%',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            boxSizing: 'border-box',
            fontFamily: 'inherit !important',
            color: 'inherit !important',
          },
          // Force text visibility
          '& div, & span, & p, & td, & th, & li': {
            color: isLightMode ? '#202124 !important' : '#e8eaed !important',
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
            margin: '16px 0',
            display: 'block',
            transition: 'transform 0.2s ease, box-shadow 0.3s ease',
            border: '1px solid rgba(0,0,0,0.08)',
            '&:hover': {
              transform: 'scale(1.01)',
              boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
            },
          },
          '& a': {
            color: isLightMode ? '#1a73e8 !important' : '#8ab4f8 !important',
            textDecoration: 'none !important',
            fontWeight: '500',
            borderRadius: '3px',
            padding: '2px 4px',
            transition: 'all 0.2s ease',
            display: 'inline',
            wordBreak: 'break-all',
            overflowWrap: 'anywhere',
            background: 'transparent',
            border: 'none',
            '&:hover': {
              background: isLightMode ? 'rgba(26, 115, 232, 0.08)' : 'rgba(138, 180, 248, 0.12)',
              textDecoration: 'underline !important',
            },
            '&:visited': {
              color: isLightMode ? '#681da8 !important' : '#c58af9 !important',
            },
          },
          '& p': {
            margin: '0 0 16px 0',
            lineHeight: 1.6,
            fontSize: '15px',
            color: isLightMode ? '#202124 !important' : '#e8eaed !important',
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: '24px 0 12px 0',
            color: isLightMode ? '#202124 !important' : '#e8eaed !important',
            fontWeight: '600',
            lineHeight: 1.3,
            letterSpacing: '-0.02em',
          },
          '& h1': { fontSize: '28px' },
          '& h2': { fontSize: '24px' },
          '& h3': { fontSize: '20px' },
          '& h4': { fontSize: '18px' },
          '& h5': { fontSize: '16px' },
          '& h6': { fontSize: '14px' },
          '& ul, & ol': {
            paddingLeft: '24px',
            margin: '12px 0',
            '& li': {
              margin: '6px 0',
              lineHeight: 1.5,
              fontSize: '15px',
              color: isLightMode ? '#202124 !important' : '#e8eaed !important',
            },
          },
          '& blockquote': {
            borderLeft: '4px solid',
            borderLeftColor: isLightMode ? '#e8eaed' : '#5f6368',
            paddingLeft: '16px',
            margin: '20px 0',
            fontStyle: 'italic',
            background: isLightMode ? '#f8f9fa' : 'rgba(255, 255, 255, 0.02)',
            padding: '16px',
            borderRadius: '8px',
            color: isLightMode ? '#202124 !important' : '#e8eaed !important',
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            margin: '20px 0',
            border: '1px solid',
            borderColor: isLightMode ? '#dadce0' : '#5f6368',
            borderRadius: '8px',
            overflow: 'hidden',
            fontSize: '14px',
          },
          '& th, & td': {
            padding: '12px 16px',
            border: '1px solid',
            borderColor: isLightMode ? '#e8eaed' : '#5f6368',
            textAlign: 'left',
            fontSize: '14px',
            lineHeight: 1.4,
            color: isLightMode ? '#202124 !important' : '#e8eaed !important',
          },
          '& th': {
            background: isLightMode ? '#f8f9fa' : 'rgba(255, 255, 255, 0.04)',
            fontWeight: '600',
            color: isLightMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
          },
          // Professional email container styling
          '& div': {
            '&[style*="background"], &[bgcolor]': {
              borderRadius: '12px',
              padding: '20px',
              margin: '16px 0',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              border: '1px solid rgba(0,0,0,0.06)',
            },
          },
          // Whop-specific email styling
          '& .whop, & [class*="whop"], & [style*="whop"]': {
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important',
          },
          // Professional newsletter styling
          '& .newsletter, & .announcement, & [class*="newsletter"]': {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            margin: '16px 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e9ecef',
          },
          // Logo and branding styling
          '& .logo, & [class*="logo"], & img[alt*="logo"], & img[alt*="Logo"]': {
            maxWidth: '200px !important',
            height: 'auto !important',
            margin: '16px auto !important',
            display: 'block !important',
            borderRadius: '8px',
          },
          // Header sections styling
          '& .header, & [class*="header"], & [role="banner"]': {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '24px',
            borderRadius: '12px 12px 0 0',
            textAlign: 'center',
            margin: '0 0 20px 0',
          },
          // Content sections styling
          '& .content-section, & [class*="content"]': {
            padding: '20px',
            margin: '16px 0',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
          },
          // Announcement styling
          '& .announcement, & [class*="announcement"]': {
            background: 'linear-gradient(135deg, #ff6b6b, #ee5a5a)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            margin: '20px 0',
            boxShadow: '0 4px 12px rgba(255,107,107,0.3)',
            '& a': {
              color: 'white !important',
              textDecoration: 'underline !important',
            },
          },
          // Gmail-style quote styling
          '& .gmail_quote, & .yahoo_quoted, & [class*="quote"]': {
            borderLeft: '3px solid #dadce0',
            paddingLeft: '16px',
            margin: '20px 0',
            color: isLightMode ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            background: isLightMode ? '#f8f9fa' : 'rgba(255, 255, 255, 0.02)',
            padding: '16px',
            borderRadius: '8px',
          },
          // Professional button styling
          '& .button, & .btn, & a[style*="button"], & a[style*="background"]': {
            display: 'inline-block !important',
            padding: '12px 24px !important',
            borderRadius: '8px !important',
            textDecoration: 'none !important',
            fontWeight: '600 !important',
            textAlign: 'center',
            margin: '12px 8px 12px 0 !important',
            transition: 'all 0.3s ease !important',
            fontSize: '14px !important',
            lineHeight: '1.4 !important',
            border: 'none !important',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24) !important',
            '&:hover': {
              transform: 'translateY(-1px) !important',
              boxShadow: '0 4px 8px rgba(0,0,0,0.16), 0 2px 4px rgba(0,0,0,0.24) !important',
            },
          },
          // "View post" button styling (Whop specific)
          '& a[href*="post"], & .view-post, & [class*="view-post"]': {
            background: 'linear-gradient(135deg, #4285f4, #1a73e8) !important',
            color: 'white !important',
            padding: '12px 24px !important',
            borderRadius: '8px !important',
            textDecoration: 'none !important',
            fontWeight: '600 !important',
            display: 'inline-block !important',
            margin: '16px 0 !important',
            boxShadow: '0 2px 8px rgba(66, 133, 244, 0.3) !important',
            '&:hover': {
              background: 'linear-gradient(135deg, #1a73e8, #1557b0) !important',
              transform: 'translateY(-2px) !important',
              boxShadow: '0 4px 12px rgba(66, 133, 244, 0.4) !important',
            },
          },
          // Unsubscribe link styling
          '& .unsubscribe, & a[href*="unsubscribe"], & [class*="unsubscribe"]': {
            color: '#666 !important',
            fontSize: '12px !important',
            textDecoration: 'none !important',
            padding: '4px 8px !important',
            borderRadius: '4px !important',
            '&:hover': {
              color: '#999 !important',
              background: 'rgba(0,0,0,0.05) !important',
            },
          },
          // Enhanced container styling
          '& .container, & .email-container, & [role="presentation"]': {
            maxWidth: '100%',
            margin: '0 auto',
            padding: '0',
          },
          // Professional center alignment
          '& center, & .center, & [align="center"]': {
            textAlign: 'center',
            margin: '0 auto',
          },
          // Enhanced responsive table styling
          '& table[width], & table[style*="width"]': {
            width: '100% !important',
            maxWidth: '100% !important',
            minWidth: 'auto !important',
          },
          '& td[width], & th[width]': {
            width: 'auto !important',
            minWidth: 'auto !important',
          },
          // Professional spacing
          '& br': {
            lineHeight: 1.8,
          },
          // Email signature styling
          '& .signature, & [class*="signature"]': {
            borderTop: '1px solid',
            borderTopColor: isLightMode ? '#e8eaed' : '#5f6368',
            paddingTop: '16px',
            marginTop: '24px',
            fontSize: '13px',
            color: isLightMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
          },
          // Professional header styling
          '& .header, & [class*="header"]': {
            borderBottom: '1px solid',
            borderBottomColor: isLightMode ? '#e8eaed' : '#5f6368',
            paddingBottom: '16px',
            marginBottom: '20px',
          },
        }}
      />
    );
  }

  // For plain text content, use the formatted parts with clickable links
  const formattedParts = formatEmailContent(content);

  console.log('📝 Rendering plain text content with', formattedParts.length, 'parts');

  return (
    <Typography
      variant="body1"
      component="div"
      sx={{
        whiteSpace: 'pre-wrap',
        color: isLightMode ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.95)',
        lineHeight: 1.7,
        fontFamily: '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        transition: 'color 0.3s ease',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        fontSize: '1rem',
        letterSpacing: '0.01em',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        // Enhanced styling for better readability
        '& strong': {
          fontWeight: 600,
          color: isLightMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
        },
        '& em': {
          fontStyle: 'italic',
          color: isLightMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.85)',
        },
        '@media (max-width: 599px)': {
          fontSize: '0.95rem',
          lineHeight: 1.6,
        },
      }}
    >
      {formattedParts.map(renderPart)}
    </Typography>
  );
};

// No custom styled wrappers; use MUI components directly



function EmailBox(): any {
  const theme = useTheme();
  const isLightMode = theme.palette.mode === 'light';
  const { email } = useParams<{ email: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailType | null>(null);
  const [emails, setEmails] = useState<EmailType[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const isMobile = useMediaQuery('(max-width:899px)');
  const emailListRef = React.useRef<HTMLDivElement>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [currentView, setCurrentView] = useState<'inbox' | 'unread' | 'archived'>('inbox');
  const [archivedEmailIds, setArchivedEmailIds] = useState<string[]>(() => {
    const saved = localStorage.getItem(`securefakemail_archived_${email}`);
    return saved ? JSON.parse(saved) : [];
  });

  // Persist archived emails
  useEffect(() => {
    if (email) {
      localStorage.setItem(`securefakemail_archived_${email}`, JSON.stringify(archivedEmailIds));
    }
  }, [archivedEmailIds, email]);

  // Filter and sort emails based on view
  const filteredEmails = emails
    .filter(e => {
      const isArchived = archivedEmailIds.includes(e.id);
      if (currentView === 'archived') return isArchived;
      if (currentView === 'unread') return !isArchived && !e.read;
      return !isArchived;
    })
    .sort((a, b) => {
      // Sort by newest first
      const dateA = new Date(a.timestamp || 0).getTime();
      const dateB = new Date(b.timestamp || 0).getTime();
      return dateB - dateA;
    });

  // Add email to localStorage if it's valid (for browser refresh persistence)
  useEffect(() => {
    if (email && email.endsWith('@wmart.in')) {
      const emails = JSON.parse(localStorage.getItem('securefakemail_generated_emails') || '[]');
      if (!emails.includes(email)) {
        emails.push(email);
        localStorage.setItem('securefakemail_generated_emails', JSON.stringify(emails));
        console.log('Added email to localStorage for persistence:', email);
      }
    }
  }, [email]);

  // Check if the email is valid and was properly generated
  useEffect(() => {
    if (!email) {
      console.warn('No email parameter found in URL');
      setUnauthorized(true);
      showSnackbar('No email address provided. Redirecting to homepage.', 'error');
      setTimeout(() => {
        navigate('/');
      }, 2000);
      return;
    }

    // Basic email format validation - be more lenient for browser refreshes
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValidFormat = emailRegex.test(email);
    const isCorrectDomain = email.endsWith('@wmart.in');

    if (!isValidFormat || !isCorrectDomain) {
      console.warn('Invalid email format or domain:', email);
      setUnauthorized(true);
      showSnackbar('Invalid email address format. Redirecting to homepage.', 'error');
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } else {
      // Email format is valid, allow access even if not in localStorage
      // This allows browser refreshes to work properly
      setUnauthorized(false);
      console.log('Email format valid, allowing access to inbox:', email);
    }
  }, [email, navigate]);

  useEffect(() => {
    if (!email || unauthorized) return;

    const fetchEmails = async () => {
      setLoading(true);
      try {
        const fetchedEmails = await emailService.getEmails(email);
        setEmails(fetchedEmails);
      } catch (error) {
        showSnackbar('Failed to fetch emails', 'error');
      }
      setLoading(false);
    };

    // Initial fetch
    fetchEmails();

    // Poll for new emails every 10 seconds
    const pollInterval = setInterval(fetchEmails, 10000);

    return () => clearInterval(pollInterval);
  }, [email, unauthorized]);

  // Update selected email when emails list changes (but only if content actually changed)
  useEffect(() => {
    if (selectedEmail && emails.length > 0) {
      const updatedSelectedEmail = emails.find(e => e.id === selectedEmail.id);
      if (updatedSelectedEmail) {
        // Only update if key properties have changed to prevent unnecessary re-renders
        const hasChanged = updatedSelectedEmail.read !== selectedEmail.read ||
          updatedSelectedEmail.important !== selectedEmail.important ||
          updatedSelectedEmail.content !== selectedEmail.content ||
          updatedSelectedEmail.subject !== selectedEmail.subject;

        if (hasChanged) {
          setSelectedEmail(updatedSelectedEmail);
        }
      } else {
        // If the selected email no longer exists, clear selection
        setSelectedEmail(null);
      }
    }
  }, [emails, selectedEmail?.id]); // Depend on emails and selectedEmail.id only

  useEffect(() => {
    document.title = selectedEmail
      ? `${selectedEmail.subject} - SecureFakeMail`
      : `Inbox - SecureFakeMail`;
  }, [selectedEmail]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const fetchedEmails = await emailService.getEmails(email || '');
      setEmails(fetchedEmails);

      // The selectedEmail will be automatically updated by the useEffect that watches emails

      showSnackbar('Inbox refreshed', 'success');
    } catch (error) {
      showSnackbar('Failed to refresh inbox', 'error');
    }
    setLoading(false);
  };

  const handleEmailClick = async (email: EmailType) => {
    setSelectedEmail(email);
    if (!email.read) {
      try {
        await emailService.markAsRead(email.to, email.id);
        const updatedEmails = emails.map(e =>
          e.id === email.id ? { ...e, read: true } : e
        );
        setEmails(updatedEmails);
      } catch (error) {
        showSnackbar('Failed to mark email as read', 'error');
      }
    }
  };

  const handleDelete = async (emailId: string) => {
    try {
      await emailService.deleteEmail(email || '', emailId);
      setEmails(emails.filter(e => e.id !== emailId));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      showSnackbar('Email deleted', 'success');
    } catch (error) {
      showSnackbar('Failed to delete email', 'error');
    }
  };

  const handleArchive = (emailId: string) => {
    setEmails(emails.map(e =>
      e.id === emailId ? { ...e, archived: true } : e
    ));
    if (selectedEmail?.id === emailId) {
      setSelectedEmail(null);
    }
    showSnackbar('Email archived', 'success');
  };

  const handleToggleImportant = async (emailId: string) => {
    try {
      await emailService.toggleImportant(email || '', emailId);
      setEmails(emails.map(e =>
        e.id === emailId ? { ...e, important: !e.important } : e
      ));
      showSnackbar('Email marked as important', 'success');
    } catch (error) {
      showSnackbar('Failed to toggle important flag', 'error');
    }
  };

  const handleDeleteEmail = async (emailId: string) => {
    try {
      await emailService.deleteEmail(email || '', emailId);
      setEmails(prev => prev.filter(email => email.id !== emailId));
      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      showSnackbar('Email deleted', 'success');
    } catch (error) {
      showSnackbar('Failed to delete email', 'error');
    }
  };

  const handleArchiveEmail = async (emailId: string) => {
    try {
      // Toggle archive status
      setArchivedEmailIds(prev => {
        const isArchived = prev.includes(emailId);
        if (isArchived) {
          return prev.filter(id => id !== emailId); // Unarchive
        } else {
          return [...prev, emailId]; // Archive
        }
      });

      if (selectedEmail?.id === emailId) {
        setSelectedEmail(null);
      }
      const isArchived = archivedEmailIds.includes(emailId);
      showSnackbar(isArchived ? 'Email moved to inbox' : 'Email archived', 'success');
    } catch (error) {
      showSnackbar('Failed to update archive status', 'error');
    }
  };

  const handleReport = (emailId: string) => {
    showSnackbar('Email reported as spam', 'info');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const copyEmailContent = () => {
    if (selectedEmail) {
      navigator.clipboard.writeText(selectedEmail.content);
      showSnackbar('Email content copied to clipboard', 'success');
    }
  };

  const handlePrint = () => {
    if (selectedEmail) {
      window.print();
    }
  };

  const copyEmailAddress = () => {
    if (email) {
      navigator.clipboard.writeText(email);
      showSnackbar('Email address copied to clipboard', 'success');
    }
  };

  const unreadCount = emails.filter(e => !e.read).length;

  // Handle back navigation on mobile
  const handleBackToList = () => {
    setSelectedEmail(null);
  };

  return (
    (<Box sx={{
      maxWidth: '1400px',
      width: '100%',
      margin: '0 auto',
      padding: { xs: '0 8px', md: '0 24px' },
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      {/* Top banner ad - shown above everything - Removed for cleaner UI */}

      <Paper sx={{
        overflow: 'hidden',
        boxShadow: isLightMode ? '0 2px 12px rgba(0, 0, 0, 0.08)' : '0 4px 20px rgba(0, 0, 0, 0.3)',
        bgcolor: isLightMode ? '#ffffff' : 'background.paper',
        position: 'relative',
        border: '1px solid',
        borderColor: isLightMode ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
        borderRadius: 2,
        mx: { xs: 0, sm: 2 },
        my: { xs: 0, sm: 2 },
        maxWidth: { lg: '1400px' },
        width: { xs: '100%', sm: 'calc(100% - 32px)' },
        margin: { xs: 0, sm: '16px auto' },
        height: { xs: 'calc(100vh - 64px)', sm: 'calc(100vh - 96px)' },
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}>
          {/* Header with Email Address - Hide when viewing email on mobile */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: { xs: 2, md: 2.5 },
              borderRadius: 0,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              justifyContent: { md: 'space-between' },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '@media (max-width: 599px)': {
                display: isMobile && selectedEmail ? 'none' : 'flex',
              },
            }}
          >
            {/* Top Navigation and Email Info - Row layout on desktop */}
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              width: { md: '100%' },
              gap: { md: 3 },
            }}>
              {/* Navigation Buttons */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: { xs: 1, md: 0 },
              }}>
                <Button
                  onClick={() => navigate('/')}
                  startIcon={<ArrowBackIcon />}
                  size="small"
                  sx={{
                    color: 'primary.contrastText',
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    },
                  }}
                >
                  Back
                </Button>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={loading}
                  size="small"
                  sx={{
                    color: 'primary.contrastText',
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    },
                    display: { xs: 'flex', md: 'none' },
                  }}
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Box>

              {/* Email Address Display */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
              }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 400,
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {email}
                </Typography>
                <IconButton
                  size="small"
                  onClick={copyEmailAddress}
                  sx={{
                    ml: 1,
                    color: 'primary.contrastText',
                  }}
                >
                  <CopyEmailIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Right side elements for desktop */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: { xs: 1, md: 0 },
            }}>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.7,
                  fontSize: '0.75rem',
                  display: { xs: 'block', md: 'inline' },
                  mr: { md: 2 },
                }}
              >
                Expires in 24 hours
              </Typography>

              <Button
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
                size="small"
                sx={{
                  color: 'primary.contrastText',
                  textTransform: 'none',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  },
                  display: { xs: 'none', md: 'flex' },
                }}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            bgcolor: isLightMode ? '#ffffff' : 'transparent',
            borderTop: '1px solid',
            borderColor: isLightMode ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
          }}>
            {/* Email List Section */}
            <Box
              ref={emailListRef}
              sx={{
                width: selectedEmail ? { xs: '100%', sm: '320px', md: '380px', lg: '420px' } : '100%',
                maxWidth: selectedEmail ? { sm: '40%', md: '420px' } : '100%',
                display: isMobile && selectedEmail ? 'none' : 'flex',
                flexDirection: 'column',
                borderRight: selectedEmail ? '1px solid' : 'none',
                borderColor: isLightMode ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                bgcolor: isLightMode ? '#ffffff' : '#1a1a1a',
                overflow: 'hidden',
                flexShrink: 0,
              }}>
              {/* Inbox Header */}
              <Box sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: isLightMode ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                bgcolor: isLightMode ? '#fafafa' : '#252525',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      color: isLightMode ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.95)',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    Inbox
                  </Typography>
                  {unreadCount > 0 && (
                    <Box
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '12px',
                        px: 1.5,
                        py: 0.25,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        minWidth: '20px',
                        textAlign: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {unreadCount}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', bgcolor: isLightMode ? '#e0e0e0' : '#424242', borderRadius: '12px', p: '3px', gap: '2px' }}>
                    <Button
                      size="small"
                      onClick={() => setCurrentView('inbox')}
                      sx={{
                        borderRadius: '10px',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 0,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: currentView === 'inbox' ? 'background.paper' : 'transparent',
                        color: currentView === 'inbox' ? 'text.primary' : 'text.secondary',
                        boxShadow: currentView === 'inbox' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                        '&:hover': { bgcolor: currentView === 'inbox' ? 'background.paper' : 'rgba(255,255,255,0.05)' },
                        transition: 'all 0.2s ease',
                        textTransform: 'capitalize'
                      }}
                    >
                      Inbox
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setCurrentView('unread')}
                      sx={{
                        borderRadius: '10px',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 0,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: currentView === 'unread' ? 'background.paper' : 'transparent',
                        color: currentView === 'unread' ? 'text.primary' : 'text.secondary',
                        boxShadow: currentView === 'unread' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                        '&:hover': { bgcolor: currentView === 'unread' ? 'background.paper' : 'rgba(255,255,255,0.05)' },
                        transition: 'all 0.2s ease',
                        textTransform: 'capitalize'
                      }}
                    >
                      Unread
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setCurrentView('archived')}
                      sx={{
                        borderRadius: '10px',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 0,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: currentView === 'archived' ? 'background.paper' : 'transparent',
                        color: currentView === 'archived' ? 'text.primary' : 'text.secondary',
                        boxShadow: currentView === 'archived' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                        '&:hover': { bgcolor: currentView === 'archived' ? 'background.paper' : 'rgba(255,255,255,0.05)' },
                        transition: 'all 0.2s ease',
                        textTransform: 'capitalize'
                      }}
                    >
                      Archived
                    </Button>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => setFilterAnchorEl(e.currentTarget)}
                    sx={{
                      ml: 1,
                      color: isLightMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        bgcolor: isLightMode ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                      }
                    }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ display: { xs: selectedEmail ? 'block' : 'none', md: 'none' } }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedEmail(null)}
                      startIcon={<ArrowBackIcon />}
                      variant="text"
                      sx={{ minWidth: 0, p: 0.5 }}
                    >
                      Back
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Email List */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : filteredEmails.length === 0 ? (
                <Box sx={{
                  p: 3,
                  textAlign: 'center',
                  color: isLightMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)'
                }}>
                  <MailOutlineIcon sx={{
                    fontSize: 48,
                    mb: 1.5,
                    opacity: 0.7,
                    color: isLightMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)'
                  }} />
                  <Typography variant="body1" fontWeight={500}>
                    {currentView === 'inbox' ? 'No emails yet' : 'No archived emails'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {currentView === 'inbox' ? 'Emails will appear here when received' : 'Archived emails will appear here'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  '&::-webkit-scrollbar': { width: '4px' },
                }}>
                  {filteredEmails.map((email) => {
                    const isSelected = email.id === selectedEmail?.id;
                    const senderName = email.from.split('<')[0].trim() || email.from.split('@')[0];
                    const timeStr = new Date(email.timestamp).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });

                    return (
                      <Box
                        key={email.id}
                        onClick={() => handleEmailClick(email)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          p: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid',
                          borderColor: isLightMode ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)',
                          bgcolor: isSelected
                            ? (isLightMode ? 'rgba(26,115,232,0.08)' : 'rgba(138,180,248,0.12)')
                            : 'transparent',
                          borderLeft: isSelected ? '4px solid' : '4px solid transparent',
                          borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                          '&:hover': {
                            bgcolor: isSelected
                              ? (isLightMode ? 'rgba(26,115,232,0.12)' : 'rgba(138,180,248,0.16)')
                              : (isLightMode ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)'),
                          },
                        }}
                      >
                        {/* Top Row: Sender and Time */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          mb: 0.5,
                          width: '100%'
                        }}>
                          <Typography
                            sx={{
                              fontWeight: email.read ? 400 : 700,
                              fontSize: '14px',
                              color: isLightMode ? '#202124' : '#e8eaed',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mr: 1,
                              flex: 1
                            }}
                          >
                            {senderName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '11px',
                              fontWeight: 500,
                              color: isSelected ? 'primary.main' : (isLightMode ? '#5f6368' : '#9aa0a6'),
                              flexShrink: 0,
                            }}
                          >
                            {timeStr}
                          </Typography>
                        </Box>

                        {/* Middle Row: Subject */}
                        <Typography
                          sx={{
                            fontWeight: email.read ? 400 : 600,
                            fontSize: '13px',
                            color: isLightMode ? '#202124' : '#e8eaed',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mb: 0.5,
                            width: '100%'
                          }}
                        >
                          {email.subject || '(no subject)'}
                        </Typography>

                        {/* Bottom Row: Preview and Actions */}
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: isLightMode ? '#5f6368' : '#9aa0a6',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              mr: 1
                            }}
                          >
                            {email.content.replace(/\n/g, ' ').substring(0, 100)}
                          </Typography>

                          <IconButton
                            size="small"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              handleToggleImportant(email.id);
                            }}
                            sx={{
                              p: 0.5,
                              ml: 'auto',
                              opacity: email.important ? 1 : 0.2,
                              '&:hover': { opacity: 1 },
                            }}
                          >
                            <StarIcon
                              sx={{
                                fontSize: 16,
                                color: email.important ? '#f4b400' : 'inherit',
                              }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Keep one ad below email list for monetization */}
                  <Box sx={{ p: 2, display: { xs: 'none', sm: 'block' } }}>
                    {React.createElement(GoogleAd as any, {
                      format: 'auto',
                      slot: '4680688448',
                      style: { height: '250px', width: '100%', maxWidth: '300px', margin: '0 auto' }
                    })}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Email Content Section */}
            <Box
              sx={{
                flex: 1,
                display: selectedEmail || !isMobile ? 'flex' : 'none',
                flexDirection: 'column',
                minWidth: 0,
                overflow: 'hidden',
                bgcolor: isLightMode ? '#fafafa' : '#1a1a1a',
                position: isMobile && selectedEmail ? 'fixed' : 'relative',
                top: isMobile && selectedEmail ? 0 : 'auto',
                left: isMobile && selectedEmail ? 0 : 'auto',
                right: isMobile && selectedEmail ? 0 : 'auto',
                bottom: isMobile && selectedEmail ? 0 : 'auto',
                zIndex: isMobile && selectedEmail ? 100 : 1,
              }}
            >
              {selectedEmail ? (
                <GmailEmailRenderer
                  email={selectedEmail}
                  isLightMode={isLightMode}
                  onBack={handleBackToList}
                  onDelete={(id: string) => handleDeleteEmail(id)}
                  onArchive={(id: string) => handleArchiveEmail(id)}
                  onToggleImportant={(id: string) => handleToggleImportant(id)}
                />
              ) : (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: isLightMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                  p: 4,
                  textAlign: 'center',
                  bgcolor: isLightMode ? '#fafafa' : '#1a1a1a',
                }}>
                  <Box sx={{ maxWidth: 400 }}>
                    <MailOutlineIcon sx={{
                      fontSize: 72,
                      mb: 3,
                      opacity: 0.4,
                      color: 'primary.main'
                    }} />
                    <Typography variant="h5" gutterBottom sx={{
                      fontWeight: 600,
                      color: isLightMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                      mb: 1.5,
                      letterSpacing: '-0.02em'
                    }}>
                      Welcome to your inbox
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: isLightMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                      lineHeight: 1.6,
                      fontSize: '1rem'
                    }}>
                      Select an email from the list to view its contents and manage your temporary messages.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper >

      {/* Quick Mobile Action Buttons - Only show when needed */}
      {
        isMobile && selectedEmail && (
          <Box sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 20,
            display: 'flex',
            gap: 1,
          }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleBackToList}
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: '50px',
                px: 2,
                boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
              }}
            >
              Back
            </Button>
          </Box>
        )
      }

      {/* Menus */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        TransitionProps={{ enter: true, exit: true }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            border: isLightMode ? 1 : 0,
            borderColor: isLightMode ? 'rgba(0, 0, 0, 0.08)' : 'divider',
            bgcolor: isLightMode ? '#ffffff' : undefined
          }
        }}
      >
        <MenuItem onClick={() => {
          if (selectedEmail) handleArchive(selectedEmail.id);
          setMenuAnchorEl(null);
        }}>
          <ArchiveIcon fontSize="small" sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedEmail) handleReport(selectedEmail.id);
          setMenuAnchorEl(null);
        }}>
          <ReportIcon fontSize="small" sx={{ mr: 1 }} />
          Report Spam
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedEmail) handleDelete(selectedEmail.id);
            setMenuAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        TransitionProps={{ enter: true, exit: true }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            border: isLightMode ? 1 : 0,
            borderColor: isLightMode ? 'rgba(0, 0, 0, 0.08)' : 'divider',
            bgcolor: isLightMode ? '#ffffff' : undefined
          }
        }}
      >
        <MenuItem onClick={() => setFilterAnchorEl(null)}>
          All
        </MenuItem>
        <MenuItem onClick={() => setFilterAnchorEl(null)}>
          Unread
        </MenuItem>
        <MenuItem onClick={() => setFilterAnchorEl(null)}>
          Important
        </MenuItem>
      </Menu>

      {/* Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionProps={{ enter: true, exit: true }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant={isLightMode ? "filled" : "standard"}
          sx={{
            width: '100%',
            transition: 'background-color 0.3s ease'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box >)
  );
}

export default EmailBox; 